// Projectiles in flight. Two kinds share the loop:
// - 'spore' (Sporulation power, amoeba-powers.md Phase C): auto-fired, SHRINKS
//   predators toward the reversal threshold instead of killing — auto-offense
//   feeds the eating loop. A shrunk-edible predator flees (predator-ai tell).
//   This system also owns spore *firing* (auto-target rhythm).
// - 'shot' (ranged weapons, A1 monorail-g79): player-aimed, plain damage.
//   Fired by attack.ts; only contact + expiry happen here.
//
// All projectiles travel via the generic velocity loop in movementSystem.

import type { Entity } from '../components'
import type { GameWorld } from '../world'
import type { EventBus } from '../events'
import { powerLevel, POWER_DEFS } from '../powers'
import { shrinkCreature } from '../growth'
import { nearestPredator } from './targeting'
import { spawnInkSplatter, spawnPigmentPuff, emitRipple } from './particles'
import { playSfx, panFor } from '../../engine/audio'
import { morphologyFrom } from '../creature-recipe'
import { dropDnaForPredator } from './damage'
import { healFromLifesteal } from './attack'
import { hitContainer } from './containers'
import { T } from '../tunables'

function fireSpore(world: GameWorld, player: Entity, target: Entity, shrink: number): void {
  const c = T.powers.sporulation
  let dx = target.position.x - player.position.x
  let dy = target.position.y - player.position.y
  const d = Math.hypot(dx, dy)
  if (d < 0.001) return
  dx /= d
  dy /= d
  const pr = player.renderable?.radius ?? T.player.baseRadius
  world.add({
    position: { x: player.position.x + dx * pr, y: player.position.y + dy * pr },
    velocity: { x: dx * c.speed, y: dy * c.speed },
    renderable: {
      shape: 'microbe',
      radius: c.sporeRadius,
      tint: POWER_DEFS['sporulation'].tint,
    },
    // GRAIN body + velocity heading = a little teardrop seed in flight.
    morphology: morphologyFrom({ body: 'grain' }, Math.random()),
    projectile: { kind: 'spore', ttl: c.ttl, shrink },
  })
}

export function projectilesSystem(world: GameWorld, player: Entity, dt: number, events: EventBus): void {
  const c = T.powers.sporulation
  const level = powerLevel(player, 'sporulation')

  // Fire on a rhythm while any predator is in acquisition range.
  player.sporeCooldown = Math.max(0, (player.sporeCooldown ?? 0) - dt)
  if (level > 0 && player.sporeCooldown === 0) {
    const target = nearestPredator(world, player.position.x, player.position.y, c.range)
    if (target) {
      const shrink = c.shrinkBase + c.shrinkPerLevel * (level - 1)
      fireSpore(world, player, target, shrink)
      player.sporeCooldown = Math.max(0.4, c.intervalBase - c.intervalPerLevel * (level - 1))
      playSfx('eat', { volume: 0.08, pan: panFor(target.position.x, player.position.x) })
    }
  }

  // Contact + expiry for projectiles in flight.
  for (const proj of [...world.with('projectile', 'position', 'renderable')]) {
    proj.projectile.ttl -= dt
    if (proj.projectile.ttl <= 0) {
      world.remove(proj)
      continue
    }
    // Dotted wake — a stochastic trail of tiny motes (~8/sec), in flight tint.
    if (Math.random() < dt * 8) {
      spawnPigmentPuff(world, proj.position.x, proj.position.y,
        proj.renderable.tint, 0.018)
    }
    for (const pred of world.with('predator', 'position', 'renderable', 'health')) {
      if (pred.absorbing || pred.chainSegment) continue
      const dx = pred.position.x - proj.position.x
      const dy = pred.position.y - proj.position.y
      const reach = pred.renderable.radius + proj.renderable.radius
      if (dx * dx + dy * dy < reach * reach) {
        if (proj.projectile.kind === 'shot') {
          const dmg = proj.projectile.damage ?? 0
          pred.health.current -= dmg
          pred.hitFlash = T.damage.hitFlashDuration
          events.emit({ type: 'damage.dealt', target: pred, amount: dmg })
          healFromLifesteal(player, dmg)
          spawnInkSplatter(world, pred.position.x, pred.position.y)
          playSfx('dash_impact', { volume: 0.25, pan: panFor(pred.position.x, player.position.x) })
          if (pred.health.current <= 0) {
            dropDnaForPredator(world, pred)
            if (world.id(pred) !== undefined) world.remove(pred)
          }
        } else {
          shrinkCreature(pred, proj.projectile.shrink ?? 0)
          pred.hitFlash = T.damage.hitFlashDuration
          spawnPigmentPuff(world, proj.position.x, proj.position.y, POWER_DEFS['sporulation'].tint, 0.05)
          // Shrink-hiss tell: a small ripple radiating from the hit.
          emitRipple(pred.position.x, pred.position.y, { strength: 0.35, lifetime: 0.5 })
        }
        world.remove(proj)
        break
      }
    }
    if (world.id(proj) === undefined) continue

    // Ranged shots also crack containers (B6); spores pass through — they
    // only know how to shrink living things.
    if (proj.projectile.kind !== 'shot') continue
    for (const box of [...world.with('container', 'position', 'renderable', 'health')]) {
      const dx = box.position.x - proj.position.x
      const dy = box.position.y - proj.position.y
      const reach = box.renderable.radius + proj.renderable.radius
      if (dx * dx + dy * dy < reach * reach) {
        hitContainer(world, box, proj.projectile.damage ?? 0, player, events)
        world.remove(proj)
        break
      }
    }
  }
}
